import { Controller } from "@hotwired/stimulus"

// Barcode scanner controller — camera-based and manual entry
// Connects to data-controller="barcode-scanner"
export default class extends Controller {
  static targets = ["input", "result", "video", "cameraContainer"]
  static values = {
    scanUrl: { type: String, default: "/barcode_configs/scan" }
  }

  connect() {
    this.stream = null
  }

  disconnect() {
    this.stopCamera()
  }

  async scan() {
    const value = this.inputTarget.value.trim()
    if (!value) return

    this.showResult("loading", "Looking up barcode...")

    try {
      const response = await fetch(`${this.scanUrlValue}?value=${encodeURIComponent(value)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({ value, source: "manual" })
      })

      if (response.ok) {
        const data = await response.json()
        this.showResult("success", `Found: ${data.entry.label || data.entry.value} (${data.entry.entity_type || "—"})`)
      } else {
        this.showResult("error", "Barcode not found in system")
      }
    } catch (error) {
      this.showResult("error", "Scan failed — check connection")
    }

    this.inputTarget.value = ""
    this.inputTarget.focus()
  }

  async openCamera() {
    if (!navigator.mediaDevices?.getUserMedia) {
      this.showResult("error", "Camera not supported in this browser")
      return
    }

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "environment" }
      })

      if (this.hasVideoTarget) {
        this.videoTarget.srcObject = this.stream
        this.videoTarget.play()
      }

      if (this.hasCameraContainerTarget) {
        this.cameraContainerTarget.classList.remove("hidden")
      }

      this.showResult("info", "Camera active — point at a barcode")
    } catch (error) {
      this.showResult("error", "Camera access denied")
    }
  }

  stopCamera() {
    if (this.stream) {
      this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
    }
    if (this.hasCameraContainerTarget) {
      this.cameraContainerTarget.classList.add("hidden")
    }
  }

  showResult(type, message) {
    if (!this.hasResultTarget) return

    this.resultTarget.classList.remove("hidden")
    const colors = {
      success: "bg-green-50 text-green-700 border-green-200",
      error: "bg-red-50 text-red-700 border-red-200",
      loading: "bg-blue-50 text-blue-700 border-blue-200",
      info: "bg-gray-50 text-gray-700 border-gray-200"
    }

    this.resultTarget.className = `mt-3 p-3 rounded-lg border text-sm ${colors[type] || colors.info}`
    this.resultTarget.textContent = message
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
